import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import {
  AuthorizationService,
  RefundLoanLevelData,
} from 'src/app/services/authorization/authorization.service';
import { TransactionEvent } from 'src/app/model/TransactionEvent';

/** Dialog data provided to Refund Loan Dialog */
export interface RefundLoanDialogData {
  transactionevent: TransactionEvent;
}

@Component({
    selector: 'app-refund-loan-dialog',
    templateUrl: './refund-loan-dialog.component.html',
    styleUrl: './refund-loan-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RefundLoanDialogComponent implements OnInit {
  /** Dependency injections */
  private readonly _dialogRef: MatDialogRef<RefundLoanDialogComponent> =
    inject(MatDialogRef);
  private readonly _authorizationService: AuthorizationService =
    inject(AuthorizationService);
  private readonly _toastrService: ToastrService = inject(ToastrService);

  /** Dialog data (initial event passed in from the table) */
  public data: RefundLoanDialogData = inject(MAT_DIALOG_DATA);

  /** Dynamic form variables */
  refundForm: FormGroup;
  isSubmitting = false;
  isLoading = true;

  /**
   * On construct, initialize the form (we'll update the max validator once
   * we load the latest event from the backend)
   */
  constructor() {
    this.refundForm = new FormGroup({
      amount: new FormControl('', [
        Validators.required,
        Validators.min(0.01),
        Validators.pattern(/^\d+(\.\d{1,2})?$/), // up to 2 decimals
      ]),
      memo: new FormControl(''),
    });
  }

  /**
   * On init, load the latest version of this TransactionEvent from the backend
   * so we get the current remaining_amount.
   */
  ngOnInit(): void {
    // If you added getTransactionEventById on AuthorizationService
    this._authorizationService
      .getTransactionEventById(this.data.transactionevent.id)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (latestEvent: TransactionEvent) => {
          // Replace dialog data with the fresh event from backend
          this.data.transactionevent = latestEvent;

          // Update max validator based on latest remaining_amount
          const amountCtrl = this.refundForm.get('amount');
          if (amountCtrl) {
            amountCtrl.setValidators([
              Validators.required,
              Validators.min(0.01),
              Validators.max(this.data.transactionevent.remaining_amount),
              Validators.pattern(/^\d+(\.\d{1,2})?$/),
            ]);
            amountCtrl.updateValueAndValidity();
          }
        },
        error: (err) => {
          console.error(
            '[REFUND-LOAN-DIALOG-COMPONENT]: Failed to load latest transaction event',
            err
          );
          this._toastrService.error(
            'Failed to load latest capture information'
          );
          this.close(false);
        },
      });
  }

  /**
   * Handle refund form submission
   */
  public submit(): void {
    if (this.refundForm.invalid || this.isLoading) {
      this._toastrService.error('Please fill in all required fields correctly');
      return;
    }

    this.isSubmitting = true;

    const formValues = this.refundForm.value;
    const refundData: RefundLoanLevelData = {
      amount: Number(formValues.amount),
      memo: formValues.memo || '',
    };

    this._authorizationService
      .refundCapture(
        this.data.transactionevent.accepted_tx_id,
        refundData,
        this.data.transactionevent.acctrefno!
      )
      .pipe(
        finalize(() =>
          setTimeout(() => {
            this.isSubmitting = false;
          }, 1000)
        )
      )
      .subscribe({
        next: ({ refund, updatedCapture }) => {
          this._toastrService.success('Refund processed successfully');

          // Optional: instant UI update based on updated capture from service
          if (updatedCapture) {
            this.data.transactionevent.remaining_amount =
              (updatedCapture as any).remaining_amount;
          } else {
            // Fallback: local subtraction
            this.data.transactionevent.remaining_amount = Math.max(
              0,
              (this.data.transactionevent.remaining_amount || 0) -
                refundData.amount
            );
          }

          // Force backend reload of authorizations + events
          this._authorizationService.refreshAuthorizationsAndEvents();

          this.close(true);
        },
        error: (err: any) => {
          console.error(
            '[REFUND-LOAN-DIALOG-COMPONENT]: Error processing refund',
            err
          );
          this._toastrService.error('Failed to process refund');
        },
      });
  }

  /**
   * Close the dialog
   */
  public close(success: boolean = false): void {
    this._dialogRef.close({ success });
  }
}
